'use client';

import { useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { fetchAPI } from '@/core/api/client';
import { useSiteConfig } from '@/core/components/providers/SiteConfigProvider';
import { hasModule } from '@/core/entitlements';

/**
 * Ceny B2B zależne od ilości (D9). Backend zwraca zalogowanej firmie `b2b_tiers` na produkcie
 * i wariancie: [{min_qty, price}] — cena jednostkowa brutto od tej ilości, już w aktywnej walucie
 * (services/localization.py b2b_tier_ladder). create_order liczy tę samą cenę po stronie serwera;
 * tutaj tylko pokazujemy ją od razu w koszyku i na stronie produktu.
 */
export type B2BTier = { min_qty: number; price: number };

export function b2bTierPrice(basePrice: number, tiers: B2BTier[] | null | undefined, quantity: number): number {
    let price = basePrice;
    let best = 0;
    for (const tier of tiers || []) {
        if (quantity >= tier.min_qty && tier.min_qty > best) {
            best = tier.min_qty;
            price = tier.price;
        }
    }
    return price;
}

/** Linia koszyka z zapamiętaną ceną bazową i progami — przelicza cenę po zmianie ilości. */
export function applyB2BTiers<T extends { price: number; quantity: number; b2b_base_price?: number; b2b_tiers?: B2BTier[] }>(item: T): T {
    if (!item.b2b_tiers?.length || item.b2b_base_price == null) return item;
    return { ...item, price: b2bTierPrice(item.b2b_base_price, item.b2b_tiers, Number(item.quantity) || 1) };
}

/** Wartość towarów netto (bez dostawy) — do minimum zamówienia grupy. */
export function cartNetValue(items: { price: number; quantity: number; tax_rate?: number | null }[]): number {
    const net = items.reduce((acc, item) => acc + (Number(item.price) * Number(item.quantity)) / (1 + (item.tax_rate ?? 23) / 100), 0);
    return Math.round(net * 100) / 100;
}

/** Minimum zamówienia netto zalogowanej firmy w walucie koszyka (`currency` tylko odświeża
 *  pobranie po zmianie waluty); null = brak minimum albo brak modułu b2b. */
export function useB2BMinOrderValue(currency?: string): number | null {
    const { data: session } = useSession();
    const siteConfig = useSiteConfig();
    const isB2B = (session?.user as any)?.type === 'b2b';
    const enabled = isB2B && hasModule(siteConfig, 'b2b');
    const [minimum, setMinimum] = useState<number | null>(null);

    useEffect(() => {
        if (!enabled) {
            setMinimum(null);
            return;
        }
        let cancelled = false;
        fetchAPI('/b2b/terms')
            .then((res: any) => { if (!cancelled) setMinimum(typeof res?.min_order_value_display === 'number' ? res.min_order_value_display : null); })
            .catch(() => { if (!cancelled) setMinimum(null); });
        return () => { cancelled = true; };
    }, [enabled, currency]);

    return minimum;
}
